import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import BasicInfo from '../../_common/BasicInfo/BasicInfo';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState } from 'react';
import { INSPEX_PATHNAME } from '../../_constants/Const';
import {
  fetchInspectionTop,
  updateInspectionTop,
} from '../../stores/inspectionTop/slice';
import {
  HISTORY_STATUSES,
  HISTORY_TYPE,
  INSPECTION_STATUSES,
  TEMPLATE_CLASS,
} from '../../_constants/inspectionConst';
import ConfirmModal from '../../_common/Modal/ConfirmModal';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { isRejected } from '@reduxjs/toolkit';
import { isGetInspectionTop } from '../../_utils/typeGuardUtils';
import { API_CODE } from '../../_constants/apiConst';
import { useNavigate } from 'react-router-dom';
import useUpdateStatus from '../../hooks/useUpdateStatus';
import useSaveHistory from '../../hooks/useSaveHistory';

/** シリアルチェック画面 */
const SerialCheck: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { error, showApiError, showTypeError, hideError } = useErrorModal();
  const updateStatus = useUpdateStatus();
  const saveHistory = useSaveHistory();
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  const { inspectionTop } = useSelector(
    (state: RootState) => state.inspectionTop,
  );
  const { userData } = useSelector((state: RootState) => state.userInfo);
  // 受入診断開始時かどうか
  const [isInspection, setIsInspection] = useState(false);
  // シリアルのチェックがされているか
  const [isSerialChecked, setIsSerialChecked] = useState(false);
  /** 表示するテンプレートの選択肢 */
  const [templateList, setTemplateList] = useState<
    { id: number; name: string; templateClass: number }[]
  >([]);
  // 選択されたテンプレートID
  const [templateId, setTemplateId] = useState<string>('');
  // 確認ダイアログの開閉フラグ
  const [isOpen, setIsOpen] = useState(false);
  // テンプレートの表示
  const [isShowTemplateList, setIsShowTemplateList] = useState(false);

  useEffect(() => {
    if (repairRequest) {
      setIsInspection(
        repairRequest.status === INSPECTION_STATUSES.INSPECTION_SERIAL_CHECK,
      );
      fetchTopData(
        repairRequest.rmaNo,
        repairRequest.status === INSPECTION_STATUSES.INSPECTION_SERIAL_CHECK,
      );
      setIsShowTemplateList(
        repairRequest.status !== INSPECTION_STATUSES.QC_REPAIRING,
      );
    }
  }, [repairRequest?.rmaNo]);

  useEffect(() => {
    // 診断Topデータ取得後に初期化
    if (templateId === '' && inspectionTop.inspectionTypeList.length > 0) {
      const { selectedInspectionTypeId, inspectionTypeList, isSerialChecked } =
        inspectionTop;
      // 通常診断への切り替えは絞り込んで表示
      // シリアルチェック済⇔通常診断に切り替え時
      const displayTemplateList = inspectionTop.isSerialChecked
        ? inspectionTypeList.filter(
            (item) => item.templateClass === TEMPLATE_CLASS.FULL,
          )
        : inspectionTypeList;

      const initialId = displayTemplateList.find(
        (temp) => temp.id === selectedInspectionTypeId,
      )
        ? selectedInspectionTypeId !== null
          ? selectedInspectionTypeId.toString()
          : ''
        : displayTemplateList[0].id.toString();

      setTemplateList(displayTemplateList);
      setTemplateId(initialId);
      setIsSerialChecked(isSerialChecked);
    }
  }, [inspectionTop]);

  // 診断Topのデータを取得
  const fetchTopData = async (rmaNo: string, isInspection: boolean) => {
    const response = await dispatch(
      fetchInspectionTop({
        query: { rmaNo },
        isInspection,
      }),
    );

    const apiCode = isInspection ? API_CODE.INSPECTION_TOP : API_CODE.QC_TOP;
    if (isRejected(response)) {
      showApiError(response.payload, apiCode, () =>
        handleCloseErrorModal(true),
      );
    } else if (!isGetInspectionTop(response.payload)) {
      showTypeError(apiCode);
    }
  };

  // 診断データ作成のためにテンプレートと類別を入れてPUTする
  const putTopData = async () => {
    return await dispatch(
      updateInspectionTop({
        newInspectionTop: {
          ...inspectionTop,
          isSerialChecked,
          inspectorName: userData.name,
          selectedInspectionTypeId: Number(templateId),
          selectedCategoryId: inspectionTop.partsCategoryList[0].id,
        },
        isInspection,
      }),
    );
  };

  /**
   * エラーダイアログのOK押下時
   * @param isNavigateRoot 修理依頼一覧に遷移するか
   */
  const handleCloseErrorModal = (isNavigateRoot = false) => {
    hideError();
    if (isNavigateRoot) {
      navigate(INSPEX_PATHNAME.RMA_SEARCH);
    }
  };

  /** 検査タイプ変更 */
  const onChangeInspectionType = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setTemplateId(event.target.value);
  };

  /** 診断開始ボタン押下 */
  const onClickStart = () => {
    setIsOpen(true);
  };

  /** 診断開始の確認ダイアログのキャンセル押下 */
  const onClickCancel = async () => {
    setIsOpen(false);
  };

  /** 診断開始の確認ダイアログのOK押下 */
  const onClickOk = async () => {
    setIsOpen(false);
    if (!repairRequest) {
      return;
    }

    // データを更新して診断項目を作成
    const response = await putTopData();
    if (isRejected(response)) {
      const apiCode = isInspection ? API_CODE.INSPECTION_TOP : API_CODE.QC_TOP;
      showApiError(response.payload, apiCode, handleCloseErrorModal);
      return;
    }

    // 受入診断のステータスを進める
    if (repairRequest.status === INSPECTION_STATUSES.INSPECTION_SERIAL_CHECK) {
      const isSuccess = await updateStatus(
        INSPECTION_STATUSES.INSPECTION_IN_PROGRESS,
        0,
        showApiError,
      );
      if (isSuccess) {
        navigate(INSPEX_PATHNAME.INSPECTION_TOP);
      }
      return;
    }

    // 出荷検査の場合はステータスを進める
    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.QC_IN_PROGRESS,
      inspectionTop.qcCount,
      showApiError,
    );

    if (!isSuccess) {
      return;
    }

    /** 操作履歴(出荷検査のCreate)を保存 */
    await saveHistory(
      repairRequest,
      HISTORY_STATUSES.QC.id,
      HISTORY_TYPE.CREATE.id,
      () => navigate(INSPEX_PATHNAME.INSPECTION_TOP),
      showApiError,
    );
  };

  return (
    <Box>
      <BasicInfo />
      <Box
        sx={{
          position: 'absolute',
          top: '55%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          width: '30%',
        }}
      >
        {repairRequest && (
          <Box width="100%" display="flex">
            <Checkbox
              sx={{ pl: 0 }}
              checked={isSerialChecked}
              onChange={() => setIsSerialChecked(!isSerialChecked)}
            />
            <Typography variant="h6" sx={{ alignSelf: 'center' }}>
              {t('LBL_SERIAL_CHECK_QUESTION', { 0: repairRequest.serialNo })}
            </Typography>
          </Box>
        )}

        {isShowTemplateList && (
          <Box width="100%" mt="2rem">
            <TextField
              select
              label={t('LBL_INSPECTION_TYPE')}
              value={templateId}
              onChange={onChangeInspectionType}
              size="small"
              fullWidth
            >
              <MenuItem value="" sx={{ display: 'none' }} />
              {templateList.map((item, i) => (
                <MenuItem key={`template_${i}`} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={onClickStart}
          disabled={!isSerialChecked || templateId === ''}
          sx={{
            mt: '2rem',
            height: '4rem',
            fontSize: theme.typography.h5.fontSize,
          }}
        >
          {repairRequest && isInspection
            ? t('BTN_START_INSPECTION')
            : t('BTN_START_QC')}
        </Button>
      </Box>
      <ConfirmModal
        isOpen={isOpen}
        onClickOk={onClickOk}
        onClickCancel={onClickCancel}
        displayMessage={
          isInspection ? t('MSG_START_INSPECTION') : t('MSG_START_QC')
        }
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default SerialCheck;
